import { fillPath, route } from "./routes.js";
import { adminUserSessionSchema } from "./users.js";
import { z } from "zod";
//#region src/sessions.ts
/**
* `/v1/admin/sessions*` — the organization-wide session list and the one-session revoke the admin
* app's Sessions tab calls.
*
* Producer: `AdminSessionsController` in `cloud/dp/core-api/src/admin/` and `SessionService` in
* `cloud/dp/core-api/src/auth/`. Consumer: the admin app's `AdminClient`. Permission keys:
* `users:read` on the list, `users:manage` on the revoke.
*
* The list row is `SessionPublicRow`, the 13 `sessionPublicColumns`, which is the same projection
* `GET /v1/admin/users/{id}` nests under `sessions`. So the row schema is imported from `users.ts`
* as {@link adminUserSessionSchema} and not respelled here. `tokenHash` is excluded at the
* repository boundary, and the row schema strips it if it ever arrives.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused. Neither route takes a body or a query, so this file declares no
* request schema.
*/
/**
* `GET /v1/admin/sessions` — the organization's live user sessions, across every user, as a bare
* array. Status 200. No query and no paging. Requires `users:read`.
*
* A row is live when it is not revoked, has not expired, has not been rotated, and its
* `sessionType` is `'user'`. MCP sessions and rotated refresh generations are not listed.
*
* Owner: `SessionService.listActiveSessions` and
* `SessionRepository.listActiveUserSessionsForOrg`.
*/
const LIST_SESSIONS_ROUTE = route("GET", "/v1/admin/sessions");
/**
* `DELETE /v1/admin/sessions/{id}` — revoke one session. Status 200 with
* `{ message: 'Session revoked' }`. Requires `users:manage`.
*
* An id that names no session in the caller's organization is also a 200: the revoke runs inside
* the org's row-level-security scope and updates zero rows, so a client cannot read "not found"
* from this route. A path id that is not a uuid is a 400 (`ParseUUIDPipe`).
*/
const REVOKE_SESSION_ROUTE = route("DELETE", "/v1/admin/sessions/{id}");
/** The session id grammar: the producer runs `ParseUUIDPipe` on `{id}` and answers 400 otherwise. */
const sessionIdPathParamSchema = z.uuid();
/**
* Fill `/v1/admin/sessions/{id}`, the path of {@link REVOKE_SESSION_ROUTE}.
*
* @param sessionId the session's uuid, a row's `id` from {@link listSessionsResponseSchema}.
*   Throws a `ZodError` when it is not a uuid: the empty string, a slug, a traversal such as
*   `../users`, a value with a space, or anything over 36 characters. No request is built for a
*   refused id.
*/
function adminSessionPath(sessionId) {
	return fillPath(REVOKE_SESSION_ROUTE.path, { id: sessionId }, { id: sessionIdPathParamSchema });
}
/**
* The body of `GET /v1/admin/sessions` — one {@link adminUserSessionSchema} row per live session.
* `userId` names the session's owner. The row carries no email or display name, so a page that
* shows who holds the session joins it against the user list.
*/
const listSessionsResponseSchema = z.array(adminUserSessionSchema);
/** The body of `DELETE /v1/admin/sessions/{id}` — status 200, `{ message: 'Session revoked' }`. */
const revokeSessionResponseSchema = z.object({ message: z.string() });
/** Every route this file names, for `route-census.ts`. */
const sessionsRoutes = Object.freeze([LIST_SESSIONS_ROUTE, REVOKE_SESSION_ROUTE]);
//#endregion
export { LIST_SESSIONS_ROUTE, REVOKE_SESSION_ROUTE, adminSessionPath, listSessionsResponseSchema, revokeSessionResponseSchema, sessionsRoutes };

//# sourceMappingURL=sessions.js.map